import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Trash2, Link, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';

export default function EmailInboxPage() {
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [assignJobId, setAssignJobId] = useState({});
  const [actionLoading, setActionLoading] = useState({});

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getUnmatchedEmails();
      setEmails(data.emails || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch unmatched emails:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const handleAssign = async (emailId) => {
    const jobId = assignJobId[emailId];
    if (!jobId) return;
    setActionLoading(prev => ({ ...prev, [emailId]: 'assign' }));
    try {
      await api.assignEmailToJob(emailId, jobId);
      setEmails(prev => prev.filter(e => e.id !== emailId));
      setTotal(prev => prev - 1);
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [emailId]: null }));
    }
  };

  const handleDismiss = async (emailId) => {
    setActionLoading(prev => ({ ...prev, [emailId]: 'dismiss' }));
    try {
      await api.dismissEmail(emailId);
      setEmails(prev => prev.filter(e => e.id !== emailId));
      setTotal(prev => prev - 1);
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [emailId]: null }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-muted-foreground">Lade Emails...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-inbox-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Email Inbox</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {total} unzugeordnete Email{total !== 1 ? 's' : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchEmails} data-testid="refresh-inbox-btn">
          Aktualisieren
        </Button>
      </div>

      {emails.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Keine unzugeordneten Emails</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {emails.map((email) => {
            const isExpanded = expanded === email.id;
            const ips = email.analysis?.ips_found || [];
            const busy = actionLoading[email.id];

            return (
              <Card key={email.id} data-testid={`unmatched-email-${email.id}`}>
                <CardContent className="p-4">
                  {/* Header row */}
                  <div
                    className="flex items-start gap-3 cursor-pointer"
                    onClick={() => setExpanded(isExpanded ? null : email.id)}
                  >
                    <Mail className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{email.from_addr}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(email.created_at).toLocaleString('de-DE')}
                        </span>
                      </div>
                      <p className="text-sm font-medium mt-0.5">{email.subject || '(kein Betreff)'}</p>
                      {ips.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {ips.map((ip, i) => (
                            <span key={i} className="text-xs bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded">
                              {ip}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="mt-4 space-y-3">
                      <div className="bg-muted/50 rounded-md p-3">
                        <pre className="text-xs whitespace-pre-wrap break-words text-foreground/80">
                          {email.body_text || '(kein Text)'}
                        </pre>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Input
                          placeholder="Job-ID eingeben..."
                          className="max-w-xs text-sm h-8"
                          value={assignJobId[email.id] || ''}
                          onChange={(e) =>
                            setAssignJobId(prev => ({ ...prev, [email.id]: e.target.value }))
                          }
                          data-testid={`assign-job-input-${email.id}`}
                        />
                        <Button
                          size="sm"
                          variant="default"
                          disabled={!assignJobId[email.id] || busy === 'assign'}
                          onClick={() => handleAssign(email.id)}
                          data-testid={`assign-email-btn-${email.id}`}
                        >
                          <Link className="w-3.5 h-3.5 mr-1.5" />
                          {busy === 'assign' ? '...' : 'Zuordnen'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          disabled={busy === 'dismiss'}
                          onClick={() => handleDismiss(email.id)}
                          data-testid={`dismiss-email-btn-${email.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          {busy === 'dismiss' ? '...' : 'Verwerfen'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
